import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Flex,
  Popconfirm,
  Skeleton,
  Tooltip,
  Typography,
  notification
} from "antd"
import React, { useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import { AutosaveStatus } from "./AutosaveStatus.tsx"
import { AvailableFieldsPanel } from "./AvailableFieldsPanel.tsx"
import { ContextStepper } from "./ContextStepper.tsx"
import { EntitySidebar } from "./EntitySidebar.tsx"
import { FieldCardPreview } from "./FieldCard.tsx"
import { InheritedFieldsPanel } from "./InheritedFieldsPanel.tsx"
import { RequirementGroupPanel } from "./RequirementGroupPanel.tsx"
import { useFormFieldRequirements } from "./useFormFieldRequirements.ts"
import {
  FORM_ENTITY_NAV,
  SUPPORTED_FORM_ENTITIES,
  type RequirementGroup
} from "./form-field-requirements.types.ts"

const { Title, Text } = Typography

function NotConfiguredYet({ label }: { label: string }) {
  return (
    <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-gray-200 py-16">
      <Text type="secondary">
        {label} requirements aren&apos;t configurable yet.
      </Text>
    </div>
  )
}

export function FormFieldRequirementsSettings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeEntity = searchParams.get("entity") || "lead"
  const urlContext = searchParams.get("context") || undefined
  const entityLabel =
    FORM_ENTITY_NAV.find((e) => e.key === activeEntity)?.label ?? activeEntity

  const supported = SUPPORTED_FORM_ENTITIES.has(activeEntity)

  const {
    data,
    loading,
    loadFailed,
    activeContext,
    draft,
    saveState,
    saveError,
    inheritedFieldKeys,
    lockedFieldsForContext,
    reload,
    selectContext,
    flush,
    addField,
    removeField,
    moveField,
    retrySave,
    resetToDefaults
  } = useFormFieldRequirements(supported ? activeEntity : "", urlContext)

  const [activeDragLabel, setActiveDragLabel] = React.useState<string | null>(
    null
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const labelByKey = useMemo(() => {
    const map = new Map<string, string>()
    data?.fields.forEach((f) => map.set(f.key, f.label))
    data?.locked_fields.forEach((f) => map.set(f.key, f.label))
    return map
  }, [data])

  const inheritedFields = useMemo(() => {
    if (!data) return []
    const locked = lockedFieldsForContext.map((f) => ({
      key: f.key,
      label: f.label
    }))
    const inherited = Array.from(inheritedFieldKeys).map((key) => ({
      key,
      label: labelByKey.get(key) ?? key
    }))
    return [...locked, ...inherited]
  }, [data, lockedFieldsForContext, inheritedFieldKeys, labelByKey])

  const usedFieldKeys = useMemo(
    () => new Set([...draft.all, ...draft.any_one, ...inheritedFieldKeys]),
    [draft, inheritedFieldKeys]
  )

  const addableFields = useMemo(
    () => (data?.fields ?? []).filter((f) => !usedFieldKeys.has(f.key)),
    [data, usedFieldKeys]
  )

  // Every edit saves itself, so leaving is never a question — but a change
  // made inside the debounce window still has to go before the screen it
  // belongs to is swapped out from under it.
  const onEntitySelect = (entity: string) => {
    flush()
    setSearchParams({ entity })
  }

  const onContextSelect = (context: string) => {
    setSearchParams({ entity: activeEntity, context })
    selectContext(context)
  }

  const configuredCount = (contextKey: string) => {
    const bucket =
      contextKey === activeContext ? draft : data?.rules[contextKey]
    return (bucket?.all.length ?? 0) + (bucket?.any_one.length ?? 0)
  }

  const onReset = async () => {
    const ok = await resetToDefaults()
    if (ok) notification.success({ message: "Context reset to defaults." })
  }

  const handleDragStart = (event: DragStartEvent) => {
    const activeData = event.active.data.current as
      | { fieldKey?: string }
      | undefined
    if (activeData?.fieldKey) {
      setActiveDragLabel(
        labelByKey.get(activeData.fieldKey) ?? activeData.fieldKey
      )
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragLabel(null)
    const { active, over } = event
    if (!over) return

    const activeData = active.data.current as
      | {
          type: "available" | "placed"
          fieldKey: string
          group?: RequirementGroup
        }
      | undefined
    const overData = over.data.current as
      | { type: "group" | "placed"; group: RequirementGroup; fieldKey?: string }
      | undefined
    if (!activeData || !overData) return

    const targetGroup = overData.group

    if (activeData.type === "available") {
      addField(targetGroup, activeData.fieldKey)
      return
    }

    const targetKeys = draft[targetGroup]
    let index = targetKeys.length
    if (overData.type === "placed" && overData.fieldKey) {
      const foundIndex = targetKeys.indexOf(overData.fieldKey)
      if (foundIndex !== -1) index = foundIndex
    }
    moveField(activeData.fieldKey, targetGroup, index)
  }

  return (
    <div className="flex flex-col gap-4">
      <Flex align="flex-start" justify="space-between">
        <div>
          <Title className="m-0 flex items-center gap-2" level={2}>
            Required Information
            <Tooltip title="Required information for operation stage">
              <InfoCircleOutlined className="text-base text-gray-400" />
            </Tooltip>
          </Title>
          <Text type="secondary">
            Define which fields are required when creating records or moving
            them through your workflow.
          </Text>
        </div>
        {supported && (
          <Flex align="center" gap="small">
            <AutosaveStatus state={saveState} />
            <Popconfirm
              cancelText="Cancel"
              description="This clears every field required for this context."
              okText="Reset"
              onConfirm={() => void onReset()}
              title="Reset to defaults?"
            >
              <Button disabled={saveState === "saving" || loading}>
                Reset to defaults
              </Button>
            </Popconfirm>
          </Flex>
        )}
      </Flex>

      <div className="flex gap-6">
        <EntitySidebar activeEntity={activeEntity} onSelect={onEntitySelect} />

        <div className="flex flex-1 flex-col gap-4">
          <div>
            <Title className="m-0" level={4}>
              {entityLabel} Requirements
            </Title>
            <Text type="secondary">
              Set which information is required when creating a{" "}
              {entityLabel.toLowerCase()}
              {supported && data && data.contexts.length > 1
                ? " or moving it to the next stage."
                : "."}
            </Text>
          </div>

          {!supported && <NotConfiguredYet label={entityLabel} />}

          {supported && loading && <Skeleton active paragraph={{ rows: 8 }} />}

          {supported && !loading && loadFailed && (
            <Alert
              action={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => void reload()}
                  size="small"
                >
                  Retry
                </Button>
              }
              description="Something went wrong loading these settings."
              showIcon
              type="error"
            />
          )}

          {supported && !loading && !loadFailed && data && activeContext && (
            <>
              {data.contexts.length > 1 && (
                <ContextStepper
                  activeContext={activeContext}
                  contexts={data.contexts}
                  countFor={configuredCount}
                  groupLabel={`${entityLabel} contexts`}
                  onSelect={onContextSelect}
                />
              )}

              {saveError && (
                <Alert
                  action={
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => void retrySave()}
                      size="small"
                    >
                      Retry
                    </Button>
                  }
                  description={saveError}
                  message="These changes were not saved."
                  showIcon
                  type="error"
                />
              )}

              <div>
                <Text className="font-semibold">
                  {data.contexts.find((c) => c.key === activeContext)?.label}
                </Text>
                <Text className="block text-xs" type="secondary">
                  These requirements must be satisfied before a{" "}
                  {entityLabel.toLowerCase()} reaches this point.
                </Text>
              </div>

              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                sensors={sensors}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
                  <AvailableFieldsPanel
                    fields={data.fields}
                    usedFieldKeys={usedFieldKeys}
                  />

                  <div className="flex flex-col gap-3">
                    <InheritedFieldsPanel fields={inheritedFields} />

                    <RequirementGroupPanel
                      addableFields={addableFields}
                      description="Every field in this group must be completed."
                      fieldKeys={draft.all}
                      group="all"
                      labelFor={(key) => labelByKey.get(key) ?? key}
                      onAdd={(key) => addField("all", key)}
                      onRemove={(key) => removeField("all", key)}
                      title="All of these fields are required"
                    />

                    <RequirementGroupPanel
                      addableFields={addableFields}
                      description="Users must complete at least one field in this group."
                      fieldKeys={draft.any_one}
                      group="any_one"
                      labelFor={(key) => labelByKey.get(key) ?? key}
                      onAdd={(key) => addField("any_one", key)}
                      onRemove={(key) => removeField("any_one", key)}
                      title="At least one of these fields is required"
                    />
                  </div>
                </div>

                <DragOverlay>
                  {activeDragLabel ? (
                    <FieldCardPreview
                      fieldKey="__overlay__"
                      label={activeDragLabel}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
